<template>
  <VegetativeMonitoringTabularView @input="$emit('input', $event)" :headers="headers" :collectionItems="collectionItems" :value="dialog" v-bind="$attrs" :showAllValues="showAllValues" :loading="loading" :isEditing.sync="isEditing" @update-show-all-values="$emit('update:showAllValues', $event)" @refresh="$emit('refresh')" />
</template>

<script>
  import {
    objectSorter,
    stringSorter,
    datetimeSorter,
  } from '@/helpers/sorting'
  import MomentUtil from '@/components/mixins/MomentUtil'
  import {
    dataAggregationTypes,
  } from '@/components/VegetativeMonitoring/PlotDetail/Tabs/surveyAggregation'
  import {
    calculateDisplayValue,
    calculateAggregateDisplayValue,
  } from '@/helpers/kvValueHelper'
  import {
    encodeTabularDataHashCode,
  } from './tabularDataHash'
  import VegetativeMonitoringTabularView from '@/components/VegetativeMonitoring/PlotDetail/TabularView/VegetativeMonitoringTabularView'
  export default {
    components: {
      VegetativeMonitoringTabularView,
    },
    mixins: [MomentUtil],
    props: {
      value: {
        type: Boolean,
        required: true,
      },
      data: {
        type: Array,
        default: () => [],
      },
      aggregateColumns: {
        type: Array,
        default: () => [],
      },
      showAllValues: {
        type: Boolean,
        default: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        dialog: false,
        collectionItems: [],
        dateFormat: 'MMM DD, YYYY',
        dataAggregationTypes: dataAggregationTypes,
        isEditing: false,
      }
    },
    computed: {
      standardHeaders() {
        return {
          select: {
            order: 0,
            text: '',
            value: 'select',
            align: 'left',
            width: 100,
          },
          datetime: {
            order: 1,
            text: 'DateTime',
            align: 'left',
            value: 'Key',
            format: date => this.formatDate(date, this.dateFormat),
            sorter: objectSorter('DateTime', datetimeSorter),
            idAccessor: x => x.Id,
            accessor: x => x.Key.SurveyDateTimeRaw,
          },
          specimenId: {
            order: 2,
            text: 'Specimen Id',
            align: 'left',
            value: 'ImportedSpeciesId',
            idAccessor: x => x.Id,
            accessor: x => x.Key.ImportedSpeciesId,
          },
          specimenCommonName: {
            order: 2,
            text: 'Common Name',
            align: 'left',
            value: 'SpeciesCommonName',
            idAccessor: x => x.Id,
            accessor: x => x.Key.SpeciesCommonName,
          },
          specimenFullLatinName: {
            order: 2,
            text: 'Latin Name',
            align: 'left',
            value: 'SpeciesFullLatinName',
            idAccessor: x => x.Id,
            accessor: x => x.Key.SpeciesFullLatinName,
          },
          variableName: {
            order: 3,
            text: 'Variable',
            align: 'left',
            value: 'VariableName',
            sorter: objectSorter('Analyte', stringSorter),
            idAccessor: x => x.Id,
            accessor: x => x.Data.VariableName,
          },
          dataType: {
            order: 4,
            text: 'Data Type',
            align: 'left',
            value: 'ValueType',
            idAccessor: x => x.Id,
            accessor: x => x.Data.ValueType,
            //sorter: objectSorter('Value', stringSorter),
          },
          dataValue: {
            order: 5,
            text: 'Value',
            align: 'left',
            value: 'Value',
            idAccessor: x => x.Id,
            accessor: x => calculateDisplayValue(x.Data),
            //sorter: objectSorter('Value', stringSorter),
          },
          units: {
            order: 6,
            text: 'Units',
            align: 'left',
            value: 'Units',
            idAccessor: x => x.Id,
            accessor: x => x.Data.PeripheralData.Units,
            //sorter: objectSorter('Value', stringSorter),
          },
          comment: {
            order: 7,
            text: 'Comment',
            align: 'left',
            value: 'Comment',
            idAccessor: x => x.Id,
            accessor: x => x.Data.Comment,
            //sorter: objectSorter('Value', stringSorter),
          },
          flags: {
            order: 8,
            text: 'Flags',
            align: 'left',
            value: 'Flags',
            idAccessor: x => x.Id,
            accessor: x => x.Data.UpdatedFlags,
            //sorter: objectSorter('Value', stringSorter),
          },
        }
      },
      aggregateHeaders() {
        let headers = {}
        Object.keys(this.dataAggregationTypes).forEach((x, i) => {
          headers[x] = {
            order: this.standardHeaders.length + i,
            text: x,
            align: 'left',
            value: x,
            accessor: y => calculateAggregateDisplayValue(y.Data, x),
          }
        })
        return headers
      },
      headers() {
        let headers = this.standardHeaders
        let result = [
          headers.select,
          headers.datetime,
          headers.specimenId,
          headers.specimenCommonName,
          headers.specimenFullLatinName,
          headers.variableName,
          headers.dataType,
          headers.dataValue,
          headers.units,
          headers.comment,
          headers.flags,
        ]
        this.aggregateColumns.forEach(x => {
          result.push(this.aggregateHeaders[x])
        })
        const sorter = (hA, hB) => {
          if (hA.order > hB.order) return 1
          if (hA.order < hB.order) return -1
          return 0
        }
        result.sort(sorter)

        return result
      },
    },
    watch: {
      value() {
        this.dialog = this.value
      },
      data() {
        this.initializeUI()
      },
    },
    methods: {
      generateItemId(x) {
        // return `${x.Key.SpecimenId}__${x.Key.SurveyDateTimeRaw}__${x.Data.VariableName}`
        const hashCode = encodeTabularDataHashCode(x._KeyHashCode)
        return `${hashCode}__${x.Data.VariableName}`
      },
      makeCollectionItems() {
        let items = this.data
        let that = this
        const result = items.map(x => {
          let hasReplicates = x.Data.Values.length > 1
          let dataValue = hasReplicates ?
            that.makeCollectionItemsWithReplicates(x.Data) :
            that.makeCollectionItemsNoReplicates(x.Data)
          let res = {
            data: {
              selected: x.selected,
              Id: this.generateItemId(x),
              Key: x.Key,
              Data: {
                ...dataValue,
              },
            },
          }
          return res
        }).filter(x => x.data.Data.VariableName != null)
        return result
      },
      makeCollectionItemsNoReplicates(data) {
        if (data.Values.length == 0) {
          return null
        }
        let dataValue = data.Values[0]
        return {
          VariableName: data.VariableName,
          Value: dataValue.Value,
          ValueType: dataValue.ValueType,
          PeripheralData: dataValue.PeripheralData || {},
          Comment: dataValue.Comment,
          UpdatedFlags: (dataValue.Flags && dataValue.Flags.map(x => x.FlagCode)) || [],
          OriginalFlags: dataValue.Flags || [],
          AggregateValues: {},
          _Trace: dataValue._Trace,
        }
      },
      makeCollectionItemsWithReplicates(data) {
        if (data.Values.length == 0) {
          return null
        }
        const hasMixedTypes = data.HasMixedTypes || false
        const values = data.Values.map(x => x.Value)
        const valueType = hasMixedTypes ? 'Mixed' : data.Values[0].ValueType
        return {
          VariableName: data.VariableName,
          Value: values,
          ValueType: valueType,
          PeripheralData: data.AggregateValues.PeripheralData || {},
          Flags: [],
          OriginalFlags: [],
          AggregateValues: data.AggregateValues.Values || {},
          _Trace: {},
        }
      },
      initializeUI() {
        this.collectionItems = this.makeCollectionItems(this.data)
      },
    },
    created() {
      this.initializeUI()
    },
  }
</script>